/**
 * @file: This file holds the controller of the Compose page.
 *
 * Compose is a page and not a dialog. A dialog covers the list it changes, and
 * the operator needs to see the order redraw while a count changes (D2).
 *
 * The controller holds no rule of its own. `ComposeState` holds the session,
 * and the controller connects it to the services, to the route and to the
 * template. The template reads one view model, which is built again after every
 * command. A getter that builds a new array on every digest would never settle.
 */

import moment from 'moment'
import { MEDIA_TYPES, defaultDurationMs, socialSourceName } from '../../../shared/media-types'
import { iconForMediaType } from 'common/media-icon'
import { screensOf } from './managers/catalogue'
import { ComposeState } from './managers/compose-state'
import { FreshLines } from './managers/fresh-lines'
import { LabelMenu } from './managers/label-menu'
import { LabelWriter } from './managers/label-writer'
import createDragDropHandler from 'common/drag-drop.util'
import './playlist-compose.component.less'

/**
 * This class names the scroll box of the sequence list, for the auto-scroll
 * while dragging. Both lists scroll, so the class names the one with the rows.
 */
const SCROLL_CONTAINER = '.playlist-compose-sequence-scroll'

class PlaylistComposeController {
  constructor (
    $scope, $element, $log, $q, $state, $timeout, $window, toaster, ApiService, SceneService,
    SceneCollectionService, SceneCollectionItemService, FileMediaService,
    SocialMediaService, ServiceAnnouncementsService, MediaLabelService
  ) {
    'ngInject'

    this._$scope = $scope
    this._$element = $element
    this._$log = $log
    this._$q = $q
    this._$state = $state
    this._$timeout = $timeout
    this._$window = $window
    // A debug view for the people who build Compose, opened from the how-to.
    this.debug = false
    this._toaster = toaster
    this._ApiService = ApiService
    this._SceneService = SceneService
    this._SceneCollectionService = SceneCollectionService
    this._SceneCollectionItemService = SceneCollectionItemService
    this._FileMediaService = FileMediaService
    this._SocialMediaService = SocialMediaService
    this._ServiceAnnouncementsService = ServiceAnnouncementsService
    this._MediaLabelService = MediaLabelService

    this.helpOpen = false
    // The takeover panel is closed until the operator opens it, as the how-to is.
    this.takeoverOpen = false
    // A write of the panel is running. The page holds the two buttons that
    // would take the panel away under it.
    this.creating = false
    // Raised on every refresh. A child that shows the same rows watches it and
    // draws itself again, because it cannot see a command of this page.
    this.revision = 0

    this._unsubscribe = null
    // The labels of a media: the writer owns the reads and the writes, and the
    // menu owns the entries of a right-click.
    this.labelWriter = new LabelWriter(this)
    this.labelMenu = new LabelMenu(this)
    // The browser asks before the tab closes only while the handler is attached,
    // so it is attached while the page is open and only speaks when there is
    // unsaved work. Back has its own question; this is for the tab's close box.
    this._warnOnLeave = event => {
      if (!this.state.dirty) {
        return
      }
      event.preventDefault()
      event.returnValue = ''
    }
    this._rebuildPending = false
    this._storedRows = ''

    this.state = new ComposeState()
    this.playlist = null
    this.search = ''
    this.hideUnused = false
    this.saving = false
    // Where a dragged row would land. The template uses it to place the line.
    this.dropTarget = { index: null, from: null }
    // The key of the media under the pointer. Its slots light up in the strip and the list.
    this.litKey = null
    // The lines a command just added, lit for a moment so the operator finds them.
    this.freshLines = new FreshLines(this)

    // The template reads one view model. An empty session builds the empty
    // page, so there is no second description of that shape.
    this.refresh()
  }

  /**
   * The screens this playlist plays on.
   *
   * The playlist can be on more than one scene, and two scenes can differ in
   * orientation. A media then needs an asset for each one. The server sends the
   * scenes with the playlist; the scene of the page is the fallback.
   */
  _screens () {
    return screensOf(this.playlist && this.playlist.scenes, this.scene)
  }

  /** The scene of the route. Every write and every navigation needs its id. */
  get _sceneId () {
    return this.scene ? this.scene.id : null
  }

  $onInit () {
    this._$window.addEventListener('beforeunload', this._warnOnLeave)
    this.load()
    this._dragDrop = createDragDropHandler({
      $scope: this._$scope,
      $element: this._$element,
      $log: this._$log,
      scrollContainerSelector: SCROLL_CONTAINER,
      rowSelector: '[data-drag-slot]',
      dragType: 'text/compose-slot',
      // `to` is the slot the row takes once it is out of the list, which is what
      // moveRow inserts at. The boundary itself would be one too far when the
      // row moves down.
      onReorder: move => this.moveRow(move.from, move.to),
      setDropTarget: target => {
        this.dropTarget = target
      }
    })
    this._dragDrop.initialize()
    this.labelWriter.attach()
    this.labelMenu.attach()
    this._watchCatalogue()
  }

  $onDestroy () {
    this._$window.removeEventListener('beforeunload', this._warnOnLeave)
    this.labelMenu.detach()
    this.labelWriter.detach()
    this.freshLines.detach()

    if (this._dragDrop) {
      this._dragDrop.destroy()
    }

    if (this._unsubscribe) {
      this._unsubscribe()
      this._unsubscribe = null
    }
  }

  /**
   * Watch the lists that feed the catalogue.
   *
   * The app fills these lists after login, so a reload straight onto Compose
   * can find them empty and offer label chips that add nothing. One
   * subscription is enough: every list service forwards to the same state, and
   * that state reports each change to every listener. A change gives the
   * session new candidates. It never touches the rows, the pins or the seed, so
   * the operator loses no work when a list arrives late.
   */
  _watchCatalogue () {
    this._unsubscribe = this._FileMediaService.subscribe(() => this._catalogueChanged())
  }

  /**
   * Rebuild the catalogue once for a burst of changes.
   *
   * The state reports every change to every listener, and the lists of the app
   * arrive one after the other. The rebuild waits for the digest, which also
   * tells the page to draw the result.
   */
  _catalogueChanged () {
    if (this._rebuildPending) {
      return
    }

    this._rebuildPending = true
    this._$scope.$applyAsync(() => {
      this._rebuildPending = false
      // A list that arrives late brings candidates with it. Those lines are new
      // to the operator as well, so they are lit like the lines of a chip.
      this._withFreshLines(() => this.state.setCatalogue(
        this.catalogue(),
        this._MediaLabelService.getAll(),
        this._SceneService.getAll()
      ))
    })
  }

  /** Fetch the playlist, its rows, the labels and the media of the catalogue. */
  load () {
    return this._$q.all([
      this._SceneCollectionService.getSceneCollectionsForScene(this._sceneId),
      this._SceneCollectionItemService.getSceneCollectionItemsForSceneCollection(this.collectionId)
    ]).then(([collections, response]) => {
      this.playlist = (collections || [])
        .find(collection => Number(collection.id) === Number(this.collectionId)) || null

      const items = this._ordered(response)
      // Save replaces every row, so the session has to know what it saw.
      this._storedRows = this._fingerprint(items)

      this.state.load(
        items, this.catalogue(), this._screens(),
        this._SceneService.getAll(), this._MediaLabelService.getAll()
      )
      this.refresh()
    }).catch(error => {
      this._$log.error(`[PlaylistCompose] load: ${error.message}`, error)
      this._toaster.error(error.message, 'Could not open Compose')
    })
  }

  /**
   * The rows in the stored order. They arrive as a graph, and the order is the
   * `order` column of the join table. Compose must show a playlist it opens
   * exactly as it is, so the list is sorted by that column and by nothing else
   * (D4).
   */
  _ordered (response) {
    return [...((response && response.items) || [])].sort((a, b) => a.order - b.order)
  }

  /**
   * Every media the operator can add, in the shape `ComposeState` reads. The
   * shared list of media types drives the loop, so a new type cannot be
   * forgotten here, and the shared rules give the name and the length.
   */
  catalogue () {
    const entriesOf = {
      file: () => this._FileMediaService.getAll().map(media => ({
        mediaType: 'file',
        mediaId: media.id,
        name: media.name,
        labels: media.labels || [],
        durationMs: defaultDurationMs('file', media.assets),
        thumbnailUrl: this._FileMediaService.getThumbnailUrl(media.id),
        orientations: this._FileMediaService.getOrientations(media.id),
        createdAt: media.createdAt
      })),
      social: () => this._SocialMediaService.getAll().map(media => ({
        mediaType: 'social',
        mediaId: media.id,
        name: socialSourceName(media),
        labels: media.labels || [],
        durationMs: defaultDurationMs('social'),
        createdAt: media.createdAt
      })),
      serviceAnnouncement: () => this._ServiceAnnouncementsService.getAll().map(media => ({
        mediaType: 'serviceAnnouncement',
        mediaId: media.id,
        name: media.name,
        labels: media.labels || [],
        durationMs: defaultDurationMs('serviceAnnouncement'),
        createdAt: media.createdAt
      }))
    }

    return MEDIA_TYPES.flatMap(mediaType => entriesOf[mediaType]())
  }

  /**
   * Build the view model the template reads. Angular calls a binding on every
   * digest, so a query that returns new objects has to run here and not in the
   * template.
   */
  refresh () {
    const state = this.state
    const shares = state.shares()
    const totalMs = state.totalMs()

    this.revision++

    this.view = {
      allMedia: state.allMedia,
      debug: this.debug ? JSON.stringify(state.debugDump(), null, 2) : null,
      groups: state.groups({ search: this.search, hideUnused: this.hideUnused }, shares),
      // The pin is a property of the slot, so the row has it. The template
      // reads it six times per row, and a call per read is a call per digest.
      rows: state.rows.map((row, slot) => ({ ...row, slot, pinned: state.isPinned(slot) })),
      shares,
      sentences: state.sentences(),
      unevenWaits: state.unevenWaits(),
      blockers: state.blockers(),
      canSave: state.canSave(),
      rules: state.rules(),
      labels: state.labels(),
      seed: state.seed,
      canUndo: state.canUndo(),
      closestRepeat: state.closestRepeat(),
      itemCount: state.itemCount(),
      mediaCount: state.mediaCount(),
      totalMs,
      loops: this.loopsOverIntervals(totalMs)
    }

    // The lines are new objects on every build, so the mark is written again.
    this.freshLines.stamp()
  }

  /**
   * How many times the loop runs over the time the playlist is scheduled. All
   * intervals count. One interval is named by its range; more are counted. The
   * calendar owns time (D10), so this only reads the intervals that are there,
   * and it does not know when a takeover covers part of one.
   */
  loopsOverIntervals (totalMs) {
    const intervals = (this.playlist && this.playlist.intervals) || []

    if (intervals.length === 0 || totalMs <= 0) {
      return null
    }

    const scheduledMs = intervals.reduce((sum, interval) => sum + moment(interval.end_datetime).diff(moment(interval.start_datetime)), 0)
    const times = Math.floor(scheduledMs / totalMs)

    if (!Number.isFinite(times) || times < 1) {
      return null
    }

    const [only] = intervals
    const range = intervals.length === 1
      ? `${moment(only.start_datetime).format('ddd HH:mm')} - ${moment(only.end_datetime).format('ddd HH:mm')}`
      : `${intervals.length} intervals`

    return { times, range }
  }

  // ===== The template calls these =====

  typeIcon (mediaType) {
    return iconForMediaType(mediaType)
  }

  perHour (outcome) {
    return outcome ? Math.round(outcome.perHour) : 0
  }

  step (line, delta) {
    this.state.setCount(line.key, line.count + delta)
    this.refresh()
  }

  /** A social line has a second knob: how many posts play each time (D3). */
  stepPosts (line, delta) {
    this.state.setPosts(line.key, line.posts + delta)
    this.refresh()
  }

  undo () {
    this.state.undo()
    this.refresh()
  }

  regenerate () {
    this.state.regenerate()
    this.refresh()
  }

  unpinAll () {
    this.state.unpinAll()
    this.refresh()
  }

  togglePin (slot) {
    this.state.togglePin(slot)
    this.refresh()
  }

  toggleLabel (labelId) {
    this._withFreshLines(() => this.state.toggleLabel(labelId))
  }

  /**
   * Add a label to the media of a row, or take it away. The menu of a row calls
   * this, and `LabelWriter` owns the write and the report of a failure.
   *
   * @param {object} media - The media of the row
   * @param {object} label - The label the operator chose
   * @param {boolean} wasChecked - Whether the media had the label
   * @returns {Promise} The write
   */
  toggleMediaLabel (media, label, wasChecked) {
    return this.labelWriter.toggle(media, label, wasChecked)
  }

  toggleAllMedia () {
    this._withFreshLines(() => this.state.toggleAllMedia())
  }

  /**
   * Run a command that changes the candidates, and light the lines it adds.
   * The mark is read from the view model, so the view model is built first.
   *
   * @param {Function} command - The command on the session
   */
  _withFreshLines (command) {
    this.freshLines.noteBefore()
    command()
    this.refresh()
    this.freshLines.markAfter()
  }

  light (key) {
    this.litKey = key
  }

  unlight () {
    this.litKey = null
  }

  toggleDebug () {
    this.debug = !this.debug
    this.refresh()
  }

  /** Put the debug data on the clipboard, so it can be pasted into a message. */
  copyDebug () {
    const clipboard = this._$window.navigator && this._$window.navigator.clipboard
    if (!clipboard || !this.view.debug) {
      this._toaster.error('Copy', 'The browser gave no clipboard. Select the text and copy it.')
      return
    }
    clipboard.writeText(this.view.debug)
      .then(() => this._toaster.success('Copied', 'The debug data is on the clipboard.'))
      .catch(() => this._toaster.error('Copy', 'The browser refused the clipboard. Select the text and copy it.'))
  }

  /**
   * Open the takeover panel. The panel owns the form and the writes; the page
   * only says whether it is on screen, and where a finished copy goes.
   */
  openTakeover () {
    this.takeoverOpen = true
  }

  closeTakeover () {
    if (this.creating) {
      return
    }

    this.takeoverOpen = false
  }

  /** A write of the panel is running, so the page holds its two buttons. */
  takeoverBusy (busy) {
    this.creating = busy
  }

  /** The copy exists. Compose opens it, so the operator can look it over. */
  takeoverCreated (collectionId) {
    this.creating = false
    this.takeoverOpen = false
    this._$state.go('manager.scene-compose', {
      id: this._sceneId,
      collectionId: String(collectionId)
    })
  }

  /** The how-to is closed until the operator asks for it, on every visit. */
  /**
   * The browser closed the how-to. The debug dump is built again on every
   * refresh while it is on, and nobody can read it behind a closed dialog, so
   * it goes off with the dialog.
   */
  closeHelp () {
    if (!this.debug) {
      return
    }

    this.debug = false
    this.refresh()
  }

  toggleHelp () {
    this.helpOpen = !this.helpOpen
  }

  moveRow (fromSlot, toSlot) {
    this.state.moveRow(fromSlot, toSlot)
    this.refresh()
  }

  /**
   * Leave without a write. Nothing reaches the server before Save (D7), so the
   * operator has to confirm that the session goes away.
   */
  back () {
    if (this.state.dirty && !this._$window.confirm('Leave Compose? The new order is not saved.')) {
      return
    }

    this._toScene()
  }

  /** One write replaces every row and every order in one transaction (D7). */
  save () {
    if (this.saving || !this.state.canSave()) {
      return this._$q.when()
    }

    this.saving = true

    return this._changedOnServer()
      .then(changed => {
        if (changed && !this._$window.confirm(
          'This playlist changed on the server since you opened Compose. Replace it anyway?'
        )) {
          this.saving = false
          return undefined
        }

        return this._write()
      })
      .catch(error => this._saveFailed(error))
  }

  /**
   * Whether the playlist holds anything else now than the session saw.
   *
   * Save replaces every row, so work a colleague did in the meantime would go
   * away without a word. An added row, a removed row, a new duration and a new
   * order are all such work, so the fingerprint holds all four.
   */
  _changedOnServer () {
    return this._SceneCollectionItemService
      .getSceneCollectionItemsForSceneCollection(this.collectionId)
      .then(response => this._fingerprint(this._ordered(response)) !== this._storedRows)
  }

  /** This query returns what the rows of the playlist are, as one string. */
  _fingerprint (items) {
    return items.map(item => `${item.id}:${item.order}:${item.duration}`).join('|')
  }

  _write () {
    return this._ApiService
      .call('scenecollectionitem/setSceneCollectionItems', this.state.payload(Number(this.collectionId)))
      .then(() => {
        this.saving = false
        this.state.markSaved()
        this._toaster.success(`${this.state.itemCount()} items in the new order`, 'Playlist saved')
        this._toScene()
      })
  }

  _saveFailed (error) {
    this.saving = false
    this._$log.error(`[PlaylistCompose] save: ${error.message}`, error)
    this._toaster.error(error.message, 'Could not save the order')
  }

  _toScene () {
    this._$state.go('manager.scene', {
      id: this._sceneId,
      collection: this.collectionId
    })
  }
}

export default {
  bindings: {
    // The route resolves both. The scene is the object, not an id.
    scene: '<',
    collectionId: '<'
  },
  controller: PlaylistComposeController,
  template: require('./playlist-compose.tpl.html').default
}

export { PlaylistComposeController }
